/*
 * แต่งภาพ round 2 — ครอปรูป (pure maths, tests/crop.test.ts; the screen is src/ui/cropView.ts).
 *
 * Nothing is cut out of the file: the kept part is Fabric's own cropX/cropY/width/height (in the picture's pixels),
 * so it can be changed or undone at any time, and the PNG/PDF/video drawing already honour it. A circle/rounded shape
 * is a clipPath made from `photoShape` (saved with the work, EXTRA_PROPS) — always remade from it when the size changes.
 *
 * The crop screen shows the picture turned by quarter turns and flipped (the "view"); the frame is kept in PICTURE
 * pixels and mapped to the screen and back, so turning or flipping never moves what is kept.
 */

export interface Rect { x: number; y: number; w: number; h: number }
/** quarter turns clockwise (as Fabric's angle) and flips, applied flip first, then turn (Fabric's order) */
export interface View { turn: 0 | 1 | 2 | 3; flipX: boolean; flipY: boolean }

/** frame shapes ('rect' = no clipPath) and how round the corners are (% of the shorter side, 0…50) */
export type Shape = 'rect' | 'circle' | 'round';
export interface PhotoShape { shape: Shape; round: number }
export const NO_SHAPE: PhotoShape = { shape: 'rect', round: 0 };
export const DEFAULT_ROUND = 12;

/** choices ก: free, the picture's own, 1:1 4:5 16:9, 9:16 3:4, A4 both ways (width ÷ height on screen) */
export const RATIOS: Array<{ id: string; name: string; r: number | null }> = [
  { id: 'free', name: 'อิสระ', r: null },
  { id: 'orig', name: 'เดิมของรูป', r: null },
  { id: '1:1', name: '1:1', r: 1 },
  { id: '4:5', name: '4:5', r: 4 / 5 },
  { id: '16:9', name: '16:9', r: 16 / 9 },
  { id: '9:16', name: '9:16', r: 9 / 16 },
  { id: '3:4', name: '3:4', r: 3 / 4 },
  { id: 'a4p', name: 'A4 ตั้ง', r: 210 / 297 },
  { id: 'a4l', name: 'A4 นอน', r: 297 / 210 }
];

/** the ratio a choice stands for, on screen (null = free) */
export function ratioOf(id: string, display: { w: number; h: number }): number | null {
  if (id === 'orig') return display.w / display.h;
  return RATIOS.find(x => x.id === id)?.r ?? null;
}

const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));
const finite = (v: unknown, d: number) => { const n = Number(v); return Number.isFinite(n) ? n : d; };

/** a saved shape (unknown → no shape; never a reason to refuse opening a work) */
export function readShape(raw: unknown): PhotoShape {
  const r = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  const shape: Shape = r.shape === 'circle' || r.shape === 'round' ? r.shape : 'rect';
  return { shape, round: shape === 'round' ? clamp(finite(r.round, DEFAULT_ROUND), 0, 50) : 0 };
}

/** the clipPath for a shape on a kept part w×h (object units, centred); null = none */
export function shapeClip(s: PhotoShape, w: number, h: number):
  | { type: 'ellipse'; rx: number; ry: number }
  | { type: 'rect'; width: number; height: number; rx: number; ry: number }
  | null {
  if (s.shape === 'circle') return { type: 'ellipse', rx: w / 2, ry: h / 2 };
  if (s.shape === 'round' && s.round > 0) { const r = Math.min(w, h) * s.round / 100; return { type: 'rect', width: w, height: h, rx: r, ry: r }; }
  return null;
}

/** the screen size of a W×H picture after the view's turn */
export const displaySize = (W: number, H: number, v: View) => (v.turn % 2 ? { w: H, h: W } : { w: W, h: H });

/** one point of the picture → the screen (flip, then turn clockwise) */
function pt(u: number, q: number, W: number, H: number, v: View): [number, number] {
  const a = v.flipX ? W - u : u, b = v.flipY ? H - q : q;
  switch (v.turn) {
    case 1: return [H - b, a];
    case 2: return [W - a, H - b];
    case 3: return [b, W - a];
    default: return [a, b];
  }
}
/** one point of the screen → the picture (the reverse of pt) */
function back(x: number, y: number, W: number, H: number, v: View): [number, number] {
  let a: number, b: number;
  switch (v.turn) {
    case 1: a = y; b = H - x; break;
    case 2: a = W - x; b = H - y; break;
    case 3: a = W - y; b = x; break;
    default: a = x; b = y;
  }
  return [v.flipX ? W - a : a, v.flipY ? H - b : b];
}
const box = (p: Array<[number, number]>): Rect => {
  const xs = p.map(c => c[0]), ys = p.map(c => c[1]);
  const x = Math.min(...xs), y = Math.min(...ys);
  return { x, y, w: Math.max(...xs) - x, h: Math.max(...ys) - y };
};
/** a kept part (picture px) → where it shows on the crop screen (display px of the turned picture) */
export const toDisplay = (r: Rect, W: number, H: number, v: View): Rect =>
  box([pt(r.x, r.y, W, H, v), pt(r.x + r.w, r.y + r.h, W, H, v)]);
/** a frame on the crop screen → the kept part in picture px */
export const toImage = (d: Rect, W: number, H: number, v: View): Rect =>
  box([back(d.x, d.y, W, H, v), back(d.x + d.w, d.y + d.h, W, H, v)]);

/** "พลิกซ้าย-ขวา" / "พลิกบน-ล่าง" as seen on screen: with a quarter turn, the screen's sideways is the picture's up-down */
export function flipView(v: View, axis: 'h' | 'v'): View {
  const pictureX = (axis === 'h') === (v.turn % 2 === 0);
  return pictureX ? { ...v, flipX: !v.flipX } : { ...v, flipY: !v.flipY };
}
export const turnView = (v: View): View => ({ ...v, turn: ((v.turn + 1) % 4) as View['turn'] });

/** the smallest kept part (picture px): small enough for a detail, never a sliver */
export const minSide = (W: number, H: number) => Math.max(8, Math.round(Math.min(W, H) * 0.02));

/** the largest frame of `ratio` inside `bounds`, centred where `r` is centred (moved in when it would stick out) */
export function fitRatio(r: Rect, ratio: number, bounds: { w: number; h: number }): Rect {
  let w = bounds.w, h = w / ratio;
  if (h > bounds.h) { h = bounds.h; w = h * ratio; }
  const cx = r.x + r.w / 2, cy = r.y + r.h / 2;
  return { x: clamp(cx - w / 2, 0, bounds.w - w), y: clamp(cy - h / 2, 0, bounds.h - h), w, h };
}

/** the largest frame of `ratio` inside `r`, same centre (a circle chosen keeps the framing the user made) */
export function fitInside(r: Rect, ratio: number): Rect {
  let w = r.w, h = w / ratio;
  if (h > r.h) { h = r.h; w = h * ratio; }
  return { x: r.x + (r.w - w) / 2, y: r.y + (r.h - h) / 2, w, h };
}

/**
 * A picture stretched on the page (scaleX ≠ scaleY) shows stretched on the crop screen too: the factors for the screen's
 * x and y (largest = 1). A ratio chosen on screen is then `r × ey ÷ ex` in the picture's own pixels.
 */
export function stretchOf(scaleX: number | undefined, scaleY: number | undefined, v: View): { ex: number; ey: number } {
  const sx = Math.abs(finite(scaleX, 1)) || 1, sy = Math.abs(finite(scaleY, 1)) || 1, m = Math.max(sx, sy);
  return v.turn % 2 ? { ex: sy / m, ey: sx / m } : { ex: sx / m, ey: sy / m };
}

export type Handle = 'move' | 'n' | 's' | 'e' | 'w' | 'nw' | 'ne' | 'sw' | 'se';

/**
 * The frame after a finger moved (dx, dy) display px from where it started (`r` = the frame at the start).
 * Stays inside the picture and at least `min` px; with a ratio only the corners resize and the ratio is kept,
 * the opposite corner staying where it is.
 */
export function dragFrame(r: Rect, h: Handle, dx: number, dy: number, bounds: { w: number; h: number }, ratio: number | null, min: number): Rect {
  if (h === 'move') return { ...r, x: clamp(r.x + dx, 0, bounds.w - r.w), y: clamp(r.y + dy, 0, bounds.h - r.h) };
  const left = h.includes('w'), right = h.includes('e'), top = h.includes('n'), bottom = h.includes('s');
  let x0 = r.x, y0 = r.y, x1 = r.x + r.w, y1 = r.y + r.h;
  if (ratio && (left || right) && (top || bottom)) {
    // the fixed corner, and the size the finger asks for along the side it moved most
    const fx = left ? x1 : x0, fy = top ? y1 : y0;
    let w = r.w + (left ? -dx : dx), hh = r.h + (top ? -dy : dy);
    if (Math.abs(w / r.w) >= Math.abs(hh / r.h)) hh = w / ratio; else w = hh * ratio;
    // room towards the moving corner, and the smallest allowed
    const roomW = left ? fx : bounds.w - fx, roomH = top ? fy : bounds.h - fy;
    const most = Math.min(roomW, roomH * ratio), least = Math.max(min, min * ratio);
    w = clamp(w, Math.min(least, most), most); hh = w / ratio;
    x0 = left ? fx - w : fx; x1 = x0 + w; y0 = top ? fy - hh : fy; y1 = y0 + hh;
    return { x: x0, y: y0, w: x1 - x0, h: y1 - y0 };
  }
  if (ratio) return r;   // a side handle cannot keep a ratio (the screen hides them)
  if (left) x0 = clamp(r.x + dx, 0, x1 - min);
  if (right) x1 = clamp(x1 + dx, x0 + min, bounds.w);
  if (top) y0 = clamp(r.y + dy, 0, y1 - min);
  if (bottom) y1 = clamp(y1 + dy, y0 + min, bounds.h);
  return { x: x0, y: y0, w: x1 - x0, h: y1 - y0 };
}

/** the kept part of a picture as Fabric has it (the whole picture when it was never cropped), inside the picture */
export function currentCrop(o: { cropX?: number; cropY?: number; width?: number; height?: number }, W: number, H: number): Rect {
  const x = clamp(finite(o.cropX, 0), 0, W - 1), y = clamp(finite(o.cropY, 0), 0, H - 1);
  const w = clamp(finite(o.width, W) || W, 1, W - x), h = clamp(finite(o.height, H) || H, 1, H - y);
  return { x, y, w, h };
}

/** the quarter turn closest to a page angle (the crop screen shows the picture the way it lies on the page) */
export const quarterOf = (angle: number | undefined): View['turn'] => ((((Math.round(finite(angle, 0) / 90)) % 4) + 4) % 4) as View['turn'];
